(() => {
  'use strict';

  const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const suggestUrl = `${rootPath}search/suggest.json`;
  const cache = new Map();

  const makeElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (typeof text === 'string') element.textContent = text;
    return element;
  };

  const productImage = (product) => {
    const featured = product && product.featured_image;
    const fallback = product && product.image;
    const candidate =
      (featured && typeof featured === 'object' && featured.url) ||
      (typeof featured === 'string' && featured) ||
      (fallback && typeof fallback === 'object' && fallback.url) ||
      (typeof fallback === 'string' && fallback) ||
      '';
    return typeof candidate === 'string' && candidate.startsWith('//') ? `https:${candidate}` : candidate;
  };

  const productPrice = (product) => {
    const price = product.price || product.price_min || '';
    if (typeof price === 'number') {
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(price / 100);
    }
    return String(price).replace(/<[^>]*>/g, '');
  };

  const initSearch = (overlay) => {
    if (!overlay || overlay.dataset.mhSearchReady === 'true') return;
    overlay.dataset.mhSearchReady = 'true';

    const header = overlay.closest('[data-mh-site-header]');
    const openButtons = document.querySelectorAll('[data-mh-search-open]');
    const closeButtons = overlay.querySelectorAll('[data-mh-search-close]');
    const form = overlay.querySelector('[data-mh-search-form]');
    const input = overlay.querySelector('[data-mh-search-input]');
    const clearButton = overlay.querySelector('[data-mh-search-clear]');
    const popular = overlay.querySelector('[data-mh-search-popular]');
    const results = overlay.querySelector('[data-mh-search-results]');
    const status = overlay.querySelector('[data-mh-search-status]');
    const viewAll = overlay.querySelector('[data-mh-search-all]');
    if (!header || !openButtons.length || !form || !input || !results || !status || !viewAll) return;

    let timer = 0;
    let requestController = null;
    let activeQuery = '';
    let previousFocus = null;

    const setStatus = (message) => {
      status.textContent = message;
    };

    const clearResults = () => {
      results.replaceChildren();
      viewAll.hidden = true;
      input.setAttribute('aria-expanded', 'false');
    };

    const showSkeleton = () => {
      results.replaceChildren();
      for (let index = 0; index < 4; index += 1) {
        const card = makeElement('div', 'mh-premium-search__product is-skeleton');
        card.setAttribute('aria-hidden', 'true');
        card.appendChild(makeElement('span', 'mh-premium-search__image'));
        const copy = makeElement('span', 'mh-premium-search__product-copy');
        copy.appendChild(makeElement('span', 'mh-premium-search__skeleton-line'));
        copy.appendChild(makeElement('span', 'mh-premium-search__skeleton-line mh-premium-search__skeleton-line--short'));
        card.appendChild(copy);
        results.appendChild(card);
      }
    };

    const renderProducts = (products, query) => {
      results.replaceChildren();
      if (!products.length) {
        const empty = makeElement('div', 'mh-premium-search__empty');
        empty.appendChild(makeElement('strong', '', `No products found for “${query}”`));
        empty.appendChild(makeElement('span', '', 'Try a colour, style, hijab, abaya or accessory name.'));
        results.appendChild(empty);
        setStatus(`No matching products found for ${query}.`);
        input.setAttribute('aria-expanded', 'true');
        return;
      }

      products.forEach((product) => {
        const link = makeElement('a', 'mh-premium-search__product');
        link.href = product.url || `${form.action}?q=${encodeURIComponent(query)}`;
        link.setAttribute('role', 'listitem');

        const media = makeElement('span', 'mh-premium-search__image');
        const imageUrl = productImage(product);
        if (imageUrl) {
          const image = document.createElement('img');
          image.src = imageUrl;
          image.alt = product.title || 'Product';
          image.loading = 'lazy';
          image.width = 132;
          image.height = 164;
          media.appendChild(image);
        }
        link.appendChild(media);

        const copy = makeElement('span', 'mh-premium-search__product-copy');
        copy.appendChild(makeElement('strong', '', product.title || 'View product'));
        const price = productPrice(product);
        if (price) copy.appendChild(makeElement('span', 'mh-premium-search__price', price));
        copy.appendChild(makeElement('small', '', 'View product →'));
        link.appendChild(copy);
        results.appendChild(link);
      });

      setStatus(`${products.length} matching ${products.length === 1 ? 'product' : 'products'} found for ${query}.`);
      viewAll.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };

    const fetchProducts = async (query) => {
      activeQuery = query;
      if (cache.has(query)) {
        renderProducts(cache.get(query), query);
        return;
      }

      if (requestController) requestController.abort();
      requestController = new AbortController();
      showSkeleton();
      setStatus(`Finding products for ${query}…`);

      const params = new URLSearchParams();
      params.set('q', query);
      params.set('resources[type]', 'product');
      params.set('resources[limit]', '6');
      params.set('resources[options][unavailable_products]', 'last');
      params.set('resources[options][fields]', 'title,product_type,variants.title,tag');

      try {
        const response = await fetch(`${suggestUrl}?${params.toString()}`, {
          headers: { Accept: 'application/json' },
          signal: requestController.signal
        });
        if (!response.ok) throw new Error(`Search request failed with ${response.status}`);
        const payload = await response.json();
        const products =
          (payload.resources && payload.resources.results && payload.resources.results.products) || [];
        if (cache.size >= 20) cache.delete(cache.keys().next().value);
        cache.set(query, products);
        if (activeQuery === query) renderProducts(products, query);
      } catch (error) {
        if (error.name === 'AbortError') return;
        clearResults();
        const fallback = makeElement('div', 'mh-premium-search__empty');
        fallback.appendChild(makeElement('strong', '', 'Search is taking longer than expected.'));
        fallback.appendChild(makeElement('span', '', 'Press Search to view the complete results page.'));
        results.appendChild(fallback);
        viewAll.hidden = false;
        setStatus('Instant results are temporarily unavailable. Use the Search button for complete results.');
      }
    };

    const updateQuery = () => {
      const query = input.value.trim();
      activeQuery = query.toLocaleLowerCase();
      clearTimeout(timer);
      if (requestController) requestController.abort();
      clearButton.hidden = !query;
      popular.hidden = Boolean(query);
      viewAll.href = query ? `${form.action}?q=${encodeURIComponent(query)}` : form.action;

      if (!query) {
        clearResults();
        setStatus('Type one or more letters to see matching products instantly.');
        return;
      }

      showSkeleton();
      setStatus(`Finding products for ${query}…`);
      timer = window.setTimeout(() => fetchProducts(activeQuery), 140);
    };

    const openSearch = (event) => {
      if (event) event.preventDefault();
      previousFocus = (event && event.currentTarget) || document.activeElement;
      overlay.hidden = false;
      document.body.classList.add('mh-search-is-open');
      openButtons.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      window.requestAnimationFrame(() => {
        overlay.classList.add('is-open');
        input.focus({ preventScroll: true });
      });
      if (input.value.trim()) updateQuery();
    };

    const closeSearch = () => {
      clearTimeout(timer);
      if (requestController) requestController.abort();
      overlay.classList.remove('is-open');
      document.body.classList.remove('mh-search-is-open');
      openButtons.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      window.setTimeout(() => {
        overlay.hidden = true;
        if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus();
      }, 180);
    };

    openButtons.forEach((button) => {
      button.setAttribute('aria-controls', overlay.id);
      button.setAttribute('aria-expanded', 'false');
      button.addEventListener('click', openSearch);
    });
    closeButtons.forEach((button) => button.addEventListener('click', closeSearch));
    input.addEventListener('input', updateQuery);
    clearButton.addEventListener('click', () => {
      input.value = '';
      updateQuery();
      input.focus();
    });
    form.addEventListener('submit', (event) => {
      if (!input.value.trim()) {
        event.preventDefault();
        input.focus();
        setStatus('Enter a product, category, colour or style to search.');
      }
    });
    overlay.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeSearch();
        return;
      }
      if (event.key === 'ArrowDown' && event.target === input) {
        const firstResult = results.querySelector('a');
        if (firstResult) {
          event.preventDefault();
          firstResult.focus();
        }
      }
    });
  };

  const initAll = () => document.querySelectorAll('[data-mh-premium-search]').forEach(initSearch);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
